import React, { useState, useMemo } from 'react';
import { Box, Heading, VStack, Badge, HStack, Text, IconButton, Input } from '@chakra-ui/react';

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Task } from '../types';
import TaskCard from './TaskCard';
import { sortTasksByPriority } from '../utils/taskSort';

const StarIcon = ({ filled = false, size = 16 }: { filled?: boolean; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

interface TaskColumnProps {
  id: string;
  title: string;
  color: string;
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onDelete?: (columnId: string) => void;
  isFinished?: boolean;
  onSetAsFinished?: (columnId: string) => void;
  dragHandle?: React.ReactNode;
}

const TaskColumn: React.FC<TaskColumnProps> = ({ id, title, color, tasks, onTaskClick, onDelete, isFinished, onSetAsFinished, dragHandle }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredTasks = useMemo(() => {
    const list = !searchQuery.trim()
      ? tasks
      : tasks.filter((t) => {
          const q = searchQuery.toLowerCase().trim();
          return (
            t.title?.toLowerCase().includes(q) ||
            t.assignedTo?.name?.toLowerCase().includes(q) ||
            t.createdBy?.name?.toLowerCase().includes(q)
          );
        });
    return sortTasksByPriority(list);
  }, [tasks, searchQuery]);

  const { setNodeRef } = useDroppable({
    id,
  });

  // Convert hex color to rgba for background with opacity
  const hexToRgba = (hex: string, alpha: number = 1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Generate color scheme from hex color
  const getColorScheme = (hexColor: string) => {
    // Use the hex color directly for accent
    const accent = hexColor;
    // Create lighter background (20% opacity)
    const bg = hexToRgba(hexColor, 0.1);
    // Create border color (30% opacity)
    const border = hexToRgba(hexColor, 0.3);
    // Use darker version for text (80% opacity)
    const text = hexToRgba(hexColor, 0.8);
    
    return { bg, border, text, accent };
  };

  const colors = getColorScheme(color.startsWith('#') ? color : `#${color}`);

  return (
    <Box
      ref={setNodeRef}
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      maxH="85vh"
      bg={colors.bg}
      borderRadius="xl"
      p={5}
      borderWidth={2}
      borderColor={colors.border}
      transition="all 0.2s"
      _hover={{ borderColor: colors.accent }}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <HStack justifyContent="space-between" mb={5} flexWrap="wrap" gap={2}>
        <HStack gap={2} flex={1}>
          {dragHandle}
          <Heading size="md" fontWeight="semibold" style={{ color: colors.text }}>
            {title}
          </Heading>
          <Badge 
            borderRadius="full"
            px={3}
            py={1}
            fontSize="xs"
            fontWeight="bold"
            color="white"
            style={{ backgroundColor: colors.accent }}
          >
            {tasks.length}
          </Badge>
          {isFinished && (
            <Box as="span" display="inline-flex" color="gold" title="Column for finished tasks">
              <StarIcon filled size={18} />
            </Box>
          )}
        </HStack>
        <HStack gap={1}>
          {onSetAsFinished && !isFinished && (
            <IconButton
              aria-label="Mark as finished column"
              size="xs"
              variant="ghost"
              colorScheme="yellow"
              onClick={() => onSetAsFinished(id)}
              title="Mark as finished column"
            >
              <StarIcon size={16} />
            </IconButton>
          )}
        {onDelete && (
          <IconButton
            aria-label="Delete column"
            size="xs"
            variant="ghost"
            onClick={() => onDelete(id)}
            style={{ color: colors.text }}
            _hover={{ bg: colors.border }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </IconButton>
        )}
        </HStack>
      </HStack>
      <Input
        placeholder="Search Tasks..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="sm"
        mb={3}
        borderRadius="md"
        bg="white"
        borderColor={colors.border}
        _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
      />
      <Box flex={1} overflowY="auto" minH="200px">
        <SortableContext items={filteredTasks.map((t) => t._id)} strategy={verticalListSortingStrategy}>
          <VStack gap={3} align="stretch">
            {filteredTasks.map((task) => (
              <TaskCard key={task._id} task={task} statusLabel={title} onClick={() => onTaskClick(task)} />
            ))}
          </VStack>
        </SortableContext>
        {filteredTasks.length === 0 && (
          <Box textAlign="center" py={8} color="gray.400">
            <Text fontSize="sm">{searchQuery.trim() ? 'No tasks match the search' : 'No tasks'}</Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TaskColumn;
